import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  MemberSaleInput,
  MemberSalePreview,
  MemberChargeReceipt,
  MemberChargeHistory,
  PackageTypeDto,
} from '@pokerroom/types';
import { api } from '../../../lib/api';
import { useFinancialCapabilities, useFinancialOperation } from './financialShared';
import { BUTTON, INPUT, PANEL, Field } from './walletShared';
export function SaleReceiptPanel({
  memberId,
  cap,
}: {
  memberId: string;
  cap: ReturnType<typeof useFinancialCapabilities>;
}) {
  return <SaleForm key={cap.formIdentity} memberId={memberId} cap={cap} />;
}
function SaleForm({
  memberId,
  cap,
}: {
  memberId: string;
  cap: ReturnType<typeof useFinancialCapabilities>;
}) {
  const { t } = useTranslation('member');
  const op = useFinancialOperation<MemberSaleInput, MemberSalePreview, MemberChargeReceipt>(
    memberId,
    'sales',
    cap.identity,
  );
  const [input, setInput] = useState<MemberSaleInput>(
    op.pending ?? { purpose: 'PASS_SALE', typeId: '', tender: 'CASH', note: '' },
  );
  const [cursor, setCursor] = useState<string | null>(null);
  const types = useQuery({
    queryKey: ['package-types', cap.venue?.id],
    queryFn: () => api.get<PackageTypeDto[]>('/members/package-types'),
  });
  const receipts = useQuery({
    queryKey: ['member-charges', memberId, cap.identity, cap.revision, cursor],
    queryFn: () =>
      api.get<MemberChargeHistory>(`/members/${memberId}/charges`, {
        params: cursor ? { cursor } : {},
      }),
    enabled: cap.can('member:charge_read'),
    retry: false,
  });
  const now = new Date(Date.now() + 8 * 3600000);
  const months = [0, 1].map((offset) =>
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1))
      .toISOString()
      .slice(0, 7),
  );
  const change = (next: MemberSaleInput) => {
    setInput(next);
    op.reset();
  };
  return (
    <section className={PANEL} aria-label={t('financial.saleTitle')}>
      {op.confirmation}
      <h3 className="font-semibold">{t('financial.saleTitle')}</h3>
      {!cap.data?.salePostingAvailable && <p role="status">{t('financial.unavailable')}</p>}
      <fieldset
        disabled={op.locked || !cap.data?.salePostingAvailable}
        className="grid gap-3 sm:grid-cols-2"
      >
        <Field label={t('financial.product')}>
          <select
            aria-label={t('financial.product')}
            className={INPUT}
            value={input.purpose}
            onChange={(e) =>
              change(
                e.target.value === 'PASS_SALE'
                  ? { purpose: 'PASS_SALE', typeId: '', tender: input.tender, note: input.note }
                  : {
                      purpose: 'PLAN_SALE',
                      month: months[0]!,
                      tender: input.tender,
                      note: input.note,
                    },
              )
            }
          >
            <option value="PASS_SALE">{t('financial.pass')}</option>
            <option value="PLAN_SALE">{t('financial.plan')}</option>
          </select>
        </Field>
        {input.purpose === 'PASS_SALE' ? (
          <Field label={t('financial.passType')}>
            <select
              aria-label={t('financial.passType')}
              className={INPUT}
              value={input.typeId}
              onChange={(e) => change({ ...input, typeId: e.target.value })}
            >
              <option value="">{t('financial.selectProduct')}</option>
              {types.data
                ?.filter((p) => p.isActive)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </Field>
        ) : (
          <Field label={t('financial.month')}>
            <select
              aria-label={t('financial.month')}
              className={INPUT}
              value={input.month}
              onChange={(e) => change({ ...input, month: e.target.value })}
            >
              {months.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </Field>
        )}
        <Field label={t('financial.tender')}>
          <select
            aria-label={t('financial.tender')}
            className={INPUT}
            value={input.tender}
            onChange={(e) => change({ ...input, tender: e.target.value as 'CASH' | 'ZIPA' })}
          >
            <option>CASH</option>
            <option>ZIPA</option>
          </select>
        </Field>
        <Field label={t('financial.reason')}>
          <input
            aria-label={t('financial.reason')}
            className={INPUT}
            value={input.note}
            maxLength={255}
            onChange={(e) => change({ ...input, note: e.target.value })}
          />
        </Field>
        <button
          className={BUTTON}
          disabled={!input.note.trim() || (input.purpose === 'PASS_SALE' && !input.typeId)}
          onClick={() => void op.load(input)}
        >
          {t('financial.preview')}
        </button>
      </fieldset>
      {op.preview && (
        <div>
          <p>
            {t('financial.amount')}: {op.preview.amount} · {input.tender}
          </p>
          <p>{t('financial.noSaleRefund')}</p>
          <button
            className={BUTTON}
            disabled={op.locked || !cap.data?.salePostingAvailable}
            onClick={() =>
              void op.submit({
                ...input,
                quoteHash: op.preview!.quoteHash,
                operationKey: crypto.randomUUID(),
              })
            }
          >
            {t('financial.confirmSale')}
          </button>
        </div>
      )}
      {op.error && <p role="alert">{t(op.error)}</p>}
      {op.pending && (
        <button
          className={BUTTON}
          disabled={op.busy || !(cap.data?.saleRecoveryAvailable ?? cap.data?.salePostingAvailable)}
          onClick={() => void op.submit(op.pending!)}
        >
          {t('financial.retry')}
        </button>
      )}
      {op.receipt && (
        <p role="status">
          {t('financial.completed')}: {op.receipt.id} · {op.receipt.netDue} · {op.receipt.tender}
        </p>
      )}
      <h4>{t('financial.saleHistory')}</h4>
      {receipts.isError && <p role="alert">{t('financial.historyFailed')}</p>}
      {receipts.data?.items
        .filter((row) => row.purpose !== 'ENTRY')
        .map((row) => (
          <p key={row.id} className="break-all">
            {row.purpose === 'PASS_SALE' ? t('financial.pass') : t('financial.plan')} · {row.netDue}{' '}
            · {row.tender} · {row.paidAt} · {row.id}
          </p>
        ))}
      {receipts.data?.nextCursor && (
        <button className={BUTTON} onClick={() => setCursor(receipts.data!.nextCursor)}>
          {t('financial.more')}
        </button>
      )}
      {cursor && (
        <button className={BUTTON} onClick={() => setCursor(null)}>
          {t('financial.firstPage')}
        </button>
      )}
    </section>
  );
}
